import Group from '@pinecast/common/Group';
import Tag from '@pinecast/common/Tag';
import {FormattedMessage} from '@pinecast/i18n';
import type {CSS, CSSProperties} from '@pinecast/styles';
import styled, {useCSS} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import InlineError from './InlineError';
import type {LocalizedReactNode, NilableLocalizedReactNode} from './types';

const LabelWrapperContext = React.createContext<boolean>(false);
const LabelContext = React.createContext<string>('');
export const useLabelContext = () => {
  const id = React.useContext(LabelContext);
  if (!id) {
    throw new Error('Tried to use label context outside label');
  }
  return id;
};

export const LabelWrapper = ({children}: {children: React.ReactNode}) => {
  const css = useCSS();
  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr',
        columnGap: '8px',
        rowGap: '8px',
      })}
    >
      <LabelWrapperContext.Provider value>
        {children}
      </LabelWrapperContext.Provider>
    </div>
  );
};

const Text = styled(
  'span',
  ({
    $oneLine,
    $oneLineCollapse,
  }: {
    $oneLine: boolean;
    $oneLineCollapse: boolean;
  }) => ({
    alignItems: 'center',
    alignSelf: $oneLine ? 'stretch' : undefined,
    display: 'flex',
    flex: $oneLineCollapse ? '0 0' : undefined,
    fontFamily: 'Monument Grotesk Bold',
    fontSize: 14,
    fontWeight: 400,
    justifyContent: $oneLine ? 'flex-end' : 'flex-start',
    paddingBottom: $oneLine ? 0 : 8,
    paddingRight: 8,
    maxHeight: $oneLine ? 32 : undefined,
    textAlign: $oneLine ? 'right' : undefined,
    whiteSpace: $oneLine ? 'nowrap' : undefined,
  }),
);
const SubText = styled('span', {
  display: 'block',
  fontFamily: DEFAULT_FONT,
  fontWeight: 400,
  marginBottom: 8,
  marginTop: -4,
});

const nativeLabelStyles: CSS = {
  display: 'block',
  gridTemplateColumns: 'min-content 1fr',
  flex: '1 1',
  fontSize: 14,
  marginBottom: 20,
};

const nativeLabelMap = new Map<string, React.ComponentType<any>>();
nativeLabelMap.set('label', styled('label', nativeLabelStyles));
nativeLabelMap.set('div', styled('div', nativeLabelStyles));

const Label = ({
  $oneLineCollapse,
  children,
  as = 'label',
  error,
  labelStyle,
  optional,
  role,
  style,
  subText,
  text,
}: {
  $oneLineCollapse?: boolean;
  children: React.ReactNode;
  as?: keyof JSX.IntrinsicElements;
  error?: React.ReactNode;
  labelStyle?: CSSProperties;
  optional?: boolean;
  role?: React.HTMLAttributes<'label'>['role'];
  style?: CSSProperties;
  subText?: NilableLocalizedReactNode;
  text: LocalizedReactNode;
}) => {
  const insideWrapper = React.useContext(LabelWrapperContext);
  const id = React.useId();

  if (!nativeLabelMap.has(as)) {
    nativeLabelMap.set(as, styled(as, {display: 'block', fontSize: 14}));
  }

  const NativeLabel = nativeLabelMap.get(as);
  if (!NativeLabel) {
    throw new Error(`Could not build styled component for ${as}`);
  }
  if (insideWrapper) {
    if (subText) {
      throw new Error('Cannot have subText inside label wrapper');
    }
    if (error) {
      throw new Error('Cannot have error inside label wrapper');
    }
  }
  const labelContents = optional ? (
    <Group spacing={4}>
      {React.isValidElement(text) ? text : <span>{text}</span>}
      <Tag size="small">
        <FormattedMessage
          description="Optional tag text for labels"
          defaultMessage="Optional"
        />
      </Tag>
    </Group>
  ) : (
    text
  );
  const labelText = (
    <Text
      id={id}
      $oneLine={insideWrapper || $oneLineCollapse || false}
      $oneLineCollapse={$oneLineCollapse || false}
      style={(!subText && labelStyle) || undefined}
    >
      {labelContents}
    </Text>
  );
  const labelBody = (
    <LabelContext.Provider value={id}>
      {React.isValidElement<any>(children)
        ? React.cloneElement(children, {
            'aria-labelledby': id,
          })
        : children}
    </LabelContext.Provider>
  );

  if ((insideWrapper || $oneLineCollapse) && !React.isValidElement(children)) {
    throw new Error(
      'Passed invalid children to Label set to $oneLine. Must be React element',
    );
  }
  if (insideWrapper) {
    return (
      <>
        {labelText}
        {labelBody}
      </>
    );
  }

  return (
    <NativeLabel
      className="Label--NativeLabel"
      role={role}
      style={
        style || insideWrapper || $oneLineCollapse
          ? {
              ...style,
              ...(insideWrapper || $oneLineCollapse
                ? {display: 'grid', gridTemplateColumns: 'min-content 1fr'}
                : null),
            }
          : undefined
      }
    >
      {labelText}
      {subText && <SubText style={labelStyle}>{subText}</SubText>}
      {labelBody}
      {Boolean(error) && <InlineError error={error} />}
    </NativeLabel>
  );
};

export default Label;
